import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Stack, Typography } from "@mui/material";
import { env } from "../../../app/config/env";
import LoginForm from "../components/LoginForm";
import { useAuth } from "../../../app/providers/AuthProvider";
import { useEmpresa } from "../../../app/providers/EmpresaProvider";

import AppIcon from "../../../assets/AppIcon.svg";

function resolveCurrentCompany(me) {
  const licenses = Array.isArray(me?.licenses) ? me.licenses : [];
  const userRole = String(me?.usuario?.user_role || "").toUpperCase();

  const licenseId =
    me?.context?.licenseId != null ? String(me.context.licenseId) : "";

  const branchId =
    userRole === "EMPLOYEE" && me?.context?.branchId != null
      ? String(me.context.branchId)
      : "";

  const matchedLicense =
    licenses.find((l) => String(l.id) === licenseId) || null;

  return {
    empresaId: licenseId,
    empresaNombre: matchedLicense?.name || "",
    sucursalId: branchId,
  };
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const appName = env.APP_NAME;

  const { login } = useAuth();
  const { setEmpresaId, setEmpresaNombre, setSucursalId } = useEmpresa();

  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  async function handleSubmit({ identifier, password }) {
    setSubmitting(true);
    setErrorMsg("");

    const fromPath = location.state?.from?.pathname;
    const nextPath =
      fromPath &&
      !fromPath.startsWith("/login") &&
      !fromPath.startsWith("/password-reset") &&
      !fromPath.startsWith("/select-client")
        ? fromPath
        : "/inicio";

    try {
      const result = await login({
        usuario: identifier,
        password,
        client: "WEB",
      });

      const me = result?.me;
      const role = String(result?.user?.user_role || me?.usuario?.user_role || "").toUpperCase();
      const needsContext = Boolean(me?.needs_context);

      if (role === "CONSULTORA") {
        setEmpresaId("");
        setEmpresaNombre("");
        setSucursalId("");
        navigate("/consultora", { replace: true });
        return;
      }

      if (needsContext) {
        setEmpresaId("");
        setEmpresaNombre("");
        setSucursalId("");
        navigate("/select-client", { replace: true, state: { next: nextPath } });
        return;
      }

      const currentCompany = resolveCurrentCompany(me);

      setEmpresaId(currentCompany.empresaId);
      setEmpresaNombre(currentCompany.empresaNombre);
      setSucursalId(currentCompany.sucursalId);

      navigate(nextPath, { replace: true });
    } catch (err) {
      const status = err?.status;

      if (status === 401) setErrorMsg("Credenciales inválidas");
      else if (status === 403) setErrorMsg(err?.message || "No tenés permisos para ingresar");
      else setErrorMsg(err?.message || "Error al iniciar sesión");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        bgcolor: "rgba(230, 230, 234, 1)",
        display: "grid",
        placeItems: "center",
        overflow: "hidden",
      }}
    >
      <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ position: "absolute", top: 20, left: 20 }}>
          <Box
            component="img"
            src={AppIcon}
            alt={`${appName} icon`}
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              objectFit: "contain",
              bgcolor: "transparent",
            }}
          />
          <Typography sx={{ fontSize: 18, fontWeight: 600, color: "rgba(2, 8, 23, 1)" }}>
            {appName}
          </Typography>
        </Stack>

        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}>
          <LoginForm
            appName={appName}
            submitting={submitting}
            onSubmit={handleSubmit}
            onForgotPassword={() => navigate("/password-reset/request")}
            errorMsg={errorMsg}
            onClearError={() => setErrorMsg("")}
          />
        </Box>
      </Box>
    </Box>
  );
}
